import React, { useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { UseCases } from "@planzone/core";
import { ProjectElementType } from "@planzone/dto";
import { MilestoneIcon } from "@common/milestoneIcon";
import { useModal } from "@root/client/modal";
import { ContextualMenu } from "@common/contextualMenu";
import { EditIcon } from "@common/editIcon";
import { TrashIcon } from "@common/trashIcon";
import { TransferIcon } from "@common/transferIcon";
import { ActivityWatcher } from "@common/activityWatcher";
import { CommentsCount } from "../../commentsCount";
import { NameEditionForm } from "./nameEditionForm";
import { Props } from "./types";
import styles from "./styles.scss";
import { SecureContextualMenu } from "@common/secureContextualMenu";

function Milestone({ milestone, project }: Props) {
  const { t } = useTranslation();
  const isLate =
    !!milestone.endDate && new Date(milestone.endDate) < new Date();
  const isCompleted = milestone.state === "DONE";
  const { pushView } = useModal();
  const [showContextualMenu, setShowContextualMenu] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  ActivityWatcher.useActivityWatcher(ref, {
    focus: () => setShowContextualMenu(!showContextualMenu),
    blur: () => setShowContextualMenu(false),
  });

  return (
    <div
      className={classNames(styles.milestone, {
        [styles.late]: isLate && !isCompleted,
        [styles.completed]: isCompleted,
      })}
    >
      <div
        className={styles.milestoneDetails}
        onClick={() =>
          pushView({
            key: UseCases.retrieveMilestone,
            data: { milestone },
          })
        }
      >
        <div className={styles.name}>
          <MilestoneIcon className={styles.icon} milestone={milestone} />
          <NameEditionForm milestone={milestone} />
        </div>
        <p className={styles.status}>
          {t(`milestoneStatuses.${milestone.state}`, milestone.state)}
        </p>
        <div className={styles.allocations} />
        {milestone.endDate ? (
          <p className={styles.text}>
            {new Date(milestone.endDate).toLocaleDateString()}
          </p>
        ) : (
          <p className={styles.text} />
        )}
        <CommentsCount
          subject={milestone.uuid}
          type="milestone"
          commentsNbr={milestone.commentsNbr}
        />
      </div>
      <div
        className={classNames(styles.contextualMenu, {
          [styles.displayContextualMenu]: showContextualMenu,
        })}
      >
        <div ref={ref}>
          <SecureContextualMenu
            context={{ milestone }}
            replacement={<div className={styles.placeholder} />}
            items={[
              {
                name: t("listsList.contextualMenu.edit"),
                useCase: UseCases.editMilestone,
                renderIcon: (className) => <EditIcon className={className} />,
                act: () =>
                  pushView({
                    key: UseCases.retrieveMilestone,
                    data: { milestone },
                  }),
              },
              {
                name: t("listsList.contextualMenu.move"),
                useCase: UseCases.moveListElement,
                renderIcon: (className) => (
                  <TransferIcon className={className} />
                ),
                act: () =>
                  pushView({
                    key: UseCases.moveListElement,
                    data: {
                      projectElementId: milestone.uuid,
                      projectElementType: ProjectElementType.MILESTONE,
                      currentParentList: milestone.list,
                      project,
                    },
                  }),
              },
              {
                name: t("listsList.contextualMenu.remove"),
                useCase: UseCases.removeMilestone,
                renderIcon: (className) => (
                  <TrashIcon
                    className={classNames(className, styles.removeIcon)}
                  />
                ),
                color: "#db5f63",
                act: () =>
                  pushView({
                    key: UseCases.removeMilestone,
                    data: milestone,
                  }),
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
}

export { Milestone };
